import { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Alert, AlertTitle, Chip, Container, Typography, Box, CircularProgress, Paper, Avatar, IconButton,
  Tabs, Tab, Snackbar, useMediaQuery
} from '@mui/material';
import { Edit, Download, ArrowBack, Delete } from '@mui/icons-material';

import { AdminRoles, ImageSize, Phase, Role, ReminderState } from '../config/DiactiveTypes';
import { getPatientFullName, getPatientHspId } from '../utils/DiactiveUtils';
import { getPatientData, startPatientCountdown, removePatientFromStudy, markPatientAsDeleted } from '../services/PatientService';
import { getPatientReminders, updateReminderStatus } from '../services/ReminderService';
import { fetchWorkouts } from '../services/WorkoutService';
import { getProfilePictureUrl } from '../services/StorageService';
import { exportSingleUserFile } from '../managers/ExcelExporter';
import { sendAppAccessEmail, sendDeletePatientEmail, sendReminderFillQuestionnairesEmail, sendReminderUploadFileEmail, sendTrainingEmail } from '../managers/MailManager';
import PatientDataTable from '../components/patients/PatientDataTable';
import WorkoutHistoryTable from '../components/patients/WorkoutHistoryTable';
import QuestionnairesTable from '../components/questionnaires/QuestionnairesTable';
import GlucoseFiles from '../components/patients/GlucoseFiles';
import ConfirmDialog from '../components/common/ConfirmDialog';
import RemindersPanel from '../components/patients/RemindersPanel';
import SentMailTable from '../components/patients/SentMailTable';
import PatientIncidentsTable from '../components/patients/PatientIncidentsTable';
import ConfirmDeleteDialog from '../components/common/ConfirmDeleteDialog';

const baseTabNames = [
  { label: "Datos del paciente", labelMobile: "Datos" },
  { label: "Cuestionarios" },
  { label: "Informes y analíticas", labelMobile: "Informes" },
  { label: "Historial de sesiones", labelMobile: "Sesiones" }
];
const mailTab = { label: "Correos", labelMobile: "Correos" };
const incidentsTab = { label: "Incidencias", labelMobile: "Incidencias" };

const resolveTabIndexFromUrl = (tabId, { canSeeMailTab, mailTabIndex, incidentsTabIndex }) => {
  if (!tabId) return 0;
  if (tabId === 'pre' || tabId === 'post') return 1;
  if (tabId === 'files') return 2;
  if (tabId === 'mail' && canSeeMailTab && mailTabIndex !== null) return mailTabIndex;
  if (tabId === 'incidents' && incidentsTabIndex !== null) return incidentsTabIndex;
  return 0;
};

const emailSendSuccessMessage = "El correo se ha enviado correctamente";

const PatientDetails = ({ role }) => {
  const { patientId, tabId } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'));
  const location = useLocation();
  const canUseDoctorMode = role === Role.SuperUser;
  const isViewAsDoctor = Boolean(location.state?.viewAsDoctor) && canUseDoctorMode;
  const hospitalIdOverride = isViewAsDoctor ? (location.state?.hospitalIdOverride ?? '') : undefined;
  const effectiveRole = isViewAsDoctor ? Role.Doctor : role;
  const canSeeMailTab = AdminRoles.includes(effectiveRole);
  const canSeeIncidentsTab =
    AdminRoles.includes(effectiveRole) || effectiveRole === Role.Doctor;
  const mailTabIndex = canSeeMailTab ? baseTabNames.length : null;
  const incidentsTabIndex = canSeeIncidentsTab
    ? baseTabNames.length + (canSeeMailTab ? 1 : 0)
    : null;
  const tabNames = [
    ...baseTabNames,
    ...(canSeeMailTab ? [mailTab] : []),
    ...(canSeeIncidentsTab ? [incidentsTab] : []),
  ];

  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: ""
  });
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [isErrorDialog, setIsErrorDialog] = useState(false);
  const [sendEmailCallback, setSendEmailCallback] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deletingPatient, setDeletingPatient] = useState(false);

  const [userData, setUserData] = useState(null);
  const [preEvalData, setPreEvalData] = useState(null);
  const [postEvalData, setPostEvalData] = useState(null);
  const [evalDataPhaseTab, setEvalDataPhaseTab] = useState(Phase.PRE);

  const [profilePictureUrl, setProfilePictureUrl] = useState(null);
  const [workouts, setWorkouts] = useState([]);
  const [tab, setTab] = useState(() =>
    resolveTabIndexFromUrl(tabId, { canSeeMailTab, mailTabIndex, incidentsTabIndex })
  );
  const [reminders, setReminders] = useState([]); // State for patient reminders

  const showPersonal = effectiveRole === Role.SuperUser || effectiveRole === Role.Doctor;
  const isPostEvalFilled = postEvalData && Object.keys(postEvalData).length > 0;

  const handleBack = () => {
    if (location.state?.fromFilteredTable) {
      navigate(-1);
    } else {
      navigate("/");
    }
  };

  useEffect(() => {
    const fetchPatient = async () => {
      setLoading(true);
      setErrorMessage(null);
      if (isViewAsDoctor && !hospitalIdOverride) {
        setErrorMessage("Selecciona un hospital para usar el modo médico");
        setLoading(false);
        return;
      }

      try {
        const { userData, preEvalData, postEvalData } = await getPatientData(effectiveRole, patientId, { hospitalIdOverride });
        setUserData(userData);
        setPreEvalData(preEvalData);
        setPostEvalData(postEvalData);
        setEvalDataPhaseTab(Object.keys(postEvalData).length > 0 ? Phase.POST : Phase.PRE);

        const profilePicUrl = await getProfilePictureUrl(patientId, ImageSize.Small, userData.profilePictureUpdatedAt);
        setProfilePictureUrl(profilePicUrl);
        const workouts = await fetchWorkouts(patientId);
        setWorkouts(workouts);

        if (effectiveRole === Role.Doctor) {
          const patientReminders = await getPatientReminders(patientId);
          setReminders(patientReminders);
        }
      } catch (error) {
        setErrorMessage(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [effectiveRole, patientId, hospitalIdOverride]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleDownloadExcel = async () => {
    await exportSingleUserFile(role, patientId);
  };

  const handleEdit = () => {
    navigate(`/patient/edit/${evalDataPhaseTab}/${patientId}`);
  };

  const handleOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeletePatient = async () => {
    setDeletingPatient(true);
    try {
      await markPatientAsDeleted(patientId);
      await sendDeletePatientEmail(userData?.patientNumber ? getPatientHspId(userData) : null);
      handleCloseDeleteDialog();
      navigate("/", { state: { successMessage: "El paciente se ha eliminado correctamente" } });
    } catch (error) {
      showErrorSnackbar(error.message);
    } finally {
      setDeletingPatient(false);
    }
  };

  const sendAppAccess = async () => {
    const hasAppAccess = await sendAppAccessEmail(userData);
    if (hasAppAccess) {
      setUserData(prev => ({ ...prev, hasAppAccess: true }));
    }
  };

  const startCountdown = async () => {
    const countdownStartedAt = await startPatientCountdown(patientId, userData.doctor);
    if (countdownStartedAt) {
      setUserData(prev => ({ ...prev, countdownStartedAt }));
      showSuccessSnackbar("La cuenta atrás ha comenzado");
    }
  };

  const removeFromStudy = async () => {
    const removedFromStudy = await removePatientFromStudy(patientId);
    if (removedFromStudy) {
      setUserData(prev => ({ ...prev, isInStudy: false }));
      showSuccessSnackbar("El paciente ha sido dado de baja");
    }
  };

  const sendReminderFillQuestionnaires = async () => {
    await sendReminderFillQuestionnairesEmail(userData, isPostEvalFilled);
  };

  const sendReminderUploadFile = async (phase) => {
    await sendReminderUploadFileEmail(userData, phase);
  };

  const sendReminderTraining = async () => {
    await sendTrainingEmail(userData);
  };

  const handleConfirmSendEmail = async () => {
    if (sendEmailCallback) {
      try {
        showSuccessSnackbar(emailSendSuccessMessage);
        await sendEmailCallback();
      } catch (error) {
        showErrorSnackbar(error.message);
      } finally {
        closeConfirmSendDialog();
      }
    }
  };

  const openConfirmSendDialog = (sendEmailCallback, isError) => {
    setOpenConfirmDialog(true);
    setSendEmailCallback(() => sendEmailCallback);
    setIsErrorDialog(isError);
  };

  const closeConfirmSendDialog = () => {
    setOpenConfirmDialog(false);
    setSendEmailCallback(null);
  };

  const handleTabChange = (_, newValue) => {
    setTab(newValue);
  };

  const handleEvalDataPhaseTabChange = (_, newValue) => {
    setEvalDataPhaseTab(newValue);
  };

  const showSuccessSnackbar = (message) => {
    setSnackbar({
      open: true,
      message,
      severity: "success"
    });
  };

  const showErrorSnackbar = (message) => {
    setSnackbar({
      open: true,
      message,
      severity: "error"
    });
  };

  const showInfoSnackbar = (message) => {
    setSnackbar({
      open: true,
      message,
      severity: "info"
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar({
      open: false,
      message: "",
      severity: ""
    });
  };

  const isDoctorPreviewMode = role === Role.SuperUser && isViewAsDoctor;
  const handleDoctorPreviewBlockedAction = () => {
    showInfoSnackbar("Esta acción está disponible solo para médicos.");
  };

  const toggleReminder = async (reminderId, completed) => {
    // Optimistically update the reminders state
    setReminders((prevReminders) =>
      prevReminders.map((reminder) =>
        reminder.id === reminderId
          ? {
              ...reminder,
              state: completed ? ReminderState.Completed : ReminderState.Active
            }
          : reminder
      )
    );

    try {
      await updateReminderStatus(patientId, reminderId, completed);
    } catch (error) {
      console.error("Error updating reminder:", error);

      // Revert the UI change if the Firestore update fails
      setReminders((prevReminders) =>
        prevReminders.map((reminder) =>
          reminder.id === reminderId
            ? {
                ...reminder,
                state: completed ? ReminderState.Active : ReminderState.Completed, // Revert state
              }
            : reminder
        )
      );
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!userData) {
    return (
      <Container sx={{ pt: 4 }}>
        <Alert severity="error">
          <AlertTitle>Error</AlertTitle>
          {errorMessage}
        </Alert>
      </Container>
    );
  }

  const isReadyForAppAccess = (
    userData.role === Role.Ctrl25 &&
    userData.daysRemaining === 0 &&
    !userData.hasAppAccess &&
    userData.isInStudy
  );
  const isReadyToStartCountdown = (
    userData.role === Role.Ctrl25 &&
    userData.countdownStartedAt === undefined &&
    userData.isInStudy
  );

  return (
    <Container maxWidth="lg" sx={{ pt: 3, pb: 3 }}>
      <Snackbar
        open={snackbar.open}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
      >
        <Alert severity={snackbar.severity} sx={{mt: 10}}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ConfirmDialog
        open={openConfirmDialog}
        title={isErrorDialog ? "Confirmar" : "Confirmar envío de correo"}
        description={
          isErrorDialog ? "¿Estás seguro? Esta acción no se puede deshacer." :
          "¿Seguro que quieres enviar el correo? Esta acción no se puede deshacer."
        }
        onCancel={closeConfirmSendDialog}
        onConfirm={handleConfirmSendEmail}
        confirmButtonColor={isErrorDialog ? "error" : "success"}
        confirmButtonText="Enviar"
      />
      <ConfirmDeleteDialog
        open={openDeleteDialog}
        onCancel={handleCloseDeleteDialog}
        onConfirm={handleDeletePatient}
        title="Eliminar paciente"
        description="¿Seguro que quieres eliminar este paciente?"
        loading={deletingPatient}
      />
      {effectiveRole === Role.Doctor && (
        <RemindersPanel
          remindersData={reminders}
          isSinglePatient={true}
          onReminderUpdate={toggleReminder}
        />
      )}
      <Paper elevation={3} sx={{ padding: 4 }}>
        {isViewAsDoctor && (
          <Box sx={{ mb: 2 }}>
            <Chip color="primary" variant="outlined" label="Viendo cómo médico" />
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleBack} aria-label="back">
            <ArrowBack color="primary" sx={{ fontSize: 40 }} />
          </IconButton>
          {
            showPersonal &&
            <Avatar alt={getPatientFullName(userData)} src={profilePictureUrl} sx={{ width: 56, height: 56, ml: 0.8 }} />
          }
          <Box sx={{ display: 'flex', flexDirection: 'column', ml: showPersonal ? 1.2 : 0.8 }}>
            <Typography variant="h5" component="h1">
              {showPersonal ? getPatientFullName(userData) : userData.nickname}
            </Typography>
          </Box>
          <IconButton onClick={handleDownloadExcel} aria-label="download" sx={{ ml: 'auto' }}>
            <Download color='primary' />
          </IconButton>
          {
            AdminRoles.includes(effectiveRole) &&
            <>
              <IconButton onClick={handleEdit} aria-label="edit">
                <Edit color='primary' />
              </IconButton>
              <IconButton onClick={handleOpenDeleteDialog} aria-label="delete">
                <Delete color='error' />
              </IconButton>
            </>
          }
        </Box>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons={isMobile ? "auto" : false}
          orientation={isMobile ? "horizontal" : "horizontal"}
          sx={{ mb: 3 }}
        >
          {tabNames.map((tabName, index) => (
            <Tab
              key={index}
              label={isMobile ? tabName.labelMobile || tabName.label : tabName.label}
            />
          ))}
        </Tabs>
        <Box>
          {tab === 0 && (
            <PatientDataTable
              userData={userData}
              preEvalData={preEvalData}
              postEvalData={postEvalData}
              phaseTab={evalDataPhaseTab}
              onPhaseTabChange={handleEvalDataPhaseTabChange}
              authUserRole={effectiveRole}
              isReadyForAppAccess={isReadyForAppAccess}
              isReadyToStartCountdown={isReadyToStartCountdown}
              showPersonal={showPersonal}
              workouts={workouts}
              isMobile={isMobile}
              isDoctorPreviewMode={isDoctorPreviewMode}
              onDoctorPreviewBlockedAction={handleDoctorPreviewBlockedAction}
              onSendAppAccess={() => openConfirmSendDialog(sendAppAccess)}
              onStartCountdown={() => openConfirmSendDialog(startCountdown)}
              onRemoveFromStudy={() => openConfirmSendDialog(removeFromStudy, true)}
            />
          )}
          {tab === 1 && (
            <QuestionnairesTable
              patientId={patientId}
              patientRole={userData.role}
              patientAge={userData.ageAtSignUp}
              authUserRole={effectiveRole}
              phase={tabId === 'pre' || tabId === 'post' ? tabId : undefined}
              isPostEvalActive={isPostEvalFilled}
              isMobile={isMobile}
              onSendReminderClick={() => openConfirmSendDialog(sendReminderFillQuestionnaires)}
            />
          )}
          {tab === 2 && (
            <GlucoseFiles
              patientId={patientId}
              role={effectiveRole}
              daysRemaining={userData.daysRemaining}
              isDoctorPreviewMode={isDoctorPreviewMode}
              onDoctorPreviewBlockedAction={handleDoctorPreviewBlockedAction}
              onSendReminderClick={(phase) => openConfirmSendDialog(() => sendReminderUploadFile(phase))}
            />
          )}
          {tab === 3 && (
            <WorkoutHistoryTable
              authUserRole={effectiveRole}
              patientRole={userData.role}
              workouts={workouts}
              canTrain={userData.canTrain}
              hasFilledQsPre={userData.hasFilledQsPre}
              glucoseFileCount={userData.glucoseFileCount}
              onSendReminderClick={() => openConfirmSendDialog(sendReminderTraining)}
            />
          )}
          {tab === mailTabIndex && canSeeMailTab && (
            <SentMailTable
              email={userData?.email}
              canResolve={AdminRoles.includes(effectiveRole)}
            />
          )}
          {tab === incidentsTabIndex && canSeeIncidentsTab && (
            <PatientIncidentsTable
              patientId={patientId}
              patientHspId={userData?.patientNumber ? getPatientHspId(userData) : null}
              role={effectiveRole}
              isDoctorPreviewMode={isDoctorPreviewMode}
              onDoctorPreviewBlockedAction={handleDoctorPreviewBlockedAction}
            />
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default PatientDetails;